import { useState } from "react";
import { supabase } from "../supabaseClient";
import { S } from "../theme";

function AuthScreen({ initialMode = "signup" }) {
  const [mode, setMode] = useState(initialMode); // signup | login | forgot
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(""); setInfo("");

    if (mode === "forgot") {
      if (!email) { setError("Enter your email."); return; }
      setLoading(true);
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: window.location.origin,
      });
      setLoading(false);
      if (error) { setError(error.message); return; }
      setInfo("Password reset email sent! Check your inbox.");
      return;
    }

    if (!email || !password) { setError("Please fill in all fields."); return; }
    if (mode === "signup" && !name.trim()) { setError("Please enter your name."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }

    setLoading(true);
    if (mode === "signup") {
      const { error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: { data: { name: name.trim() } },
      });
      setLoading(false);
      if (error) { setError(error.message); return; }
      if (window.fbq) window.fbq('track', 'Lead');
      setInfo("Account created! You can now log in.");
      setMode("login");
      setPassword("");
    } else {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      setLoading(false);
      if (error) { setError(error.message); return; }
      // onAuthStateChange in App will handle the rest
    }
  }

  async function handleGoogle() {
    setError("");
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin },
    });
    if (error) setError(error.message);
  }

  const inputStyle = { width: "100%", padding: "12px 16px", border: "1.5px solid " + S.border, borderRadius: 12, fontSize: "0.9rem", fontFamily: "'Inter', sans-serif", background: S.bg, color: S.ink, outline: "none", boxSizing: "border-box" };

  return (
    <div style={{ minHeight: "100vh", background: S.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ marginBottom: 40, textAlign: "center" }}>
        <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 900, fontSize: "1.6rem", letterSpacing: "-0.02em", color: S.ink }}>
          Track<span style={{ color: S.accent }}>My</span>Peptide
        </div>
        <p style={{ color: S.muted, fontSize: "0.85rem", marginTop: 6 }}>The peptide protocol tracker</p>
      </div>

      <div style={{ background: S.white, border: "1px solid " + S.border, borderRadius: 20, padding: 40, width: "100%", maxWidth: 420, boxShadow: "0 8px 40px rgba(0,0,0,0.06)" }}>
        {mode !== "forgot" && (
          <div style={{ display: "flex", gap: 6, marginBottom: 28, background: S.bg, borderRadius: 12, padding: 4 }}>
            {["signup","login"].map(m => (
              <button key={m} onClick={() => { setMode(m); setError(""); setInfo(""); }} style={{ flex: 1, padding: "10px", border: "none", borderRadius: 10, fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer", background: mode === m ? S.white : "transparent", color: mode === m ? S.ink : S.muted, transition: "all 0.2s", boxShadow: mode === m ? "0 1px 4px rgba(0,0,0,0.08)" : "none" }}>
                {m === "signup" ? "Sign Up" : "Log In"}
              </button>
            ))}
          </div>
        )}

        {mode !== "forgot" && (
          <>
            <button onClick={handleGoogle} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: "12px", border: "1.5px solid " + S.border, borderRadius: 100, background: S.white, fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "0.88rem", color: S.ink2, cursor: "pointer", marginBottom: 20 }}>
              <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92a8.78 8.78 0 0 0 2.68-6.62z"/><path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.02-3.7H.96v2.34A9 9 0 0 0 9 18z"/><path fill="#FBBC05" d="M3.98 10.72a5.4 5.4 0 0 1 0-3.44V4.94H.96a9 9 0 0 0 0 8.12l3.02-2.34z"/><path fill="#EA4335" d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.94l3.02 2.34C4.68 5.16 6.66 3.58 9 3.58z"/></svg>
              Continue with Google
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <div style={{ flex: 1, height: 1, background: S.border }} />
              <span style={{ fontSize: "0.72rem", color: S.muted }}>or</span>
              <div style={{ flex: 1, height: 1, background: S.border }} />
            </div>
          </>
        )}

        {mode === "forgot" && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: "1.2rem", color: S.ink, marginBottom: 6 }}>Reset your password</div>
            <p style={{ fontSize: "0.82rem", color: S.muted, lineHeight: 1.6 }}>Enter your email and we'll send you a reset link.</p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {mode === "signup" && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: "0.78rem", fontWeight: 600, color: S.ink2, marginBottom: 6 }}>Full Name</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your name" style={inputStyle} />
            </div>
          )}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: "0.78rem", fontWeight: 600, color: S.ink2, marginBottom: 6 }}>Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" style={inputStyle} />
          </div>
          {mode !== "forgot" && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: "0.78rem", fontWeight: 600, color: S.ink2, marginBottom: 6 }}>Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters" style={inputStyle} />
            </div>
          )}
          {error && <p style={{ color: "#dc2626", fontSize: "0.8rem", marginBottom: 12, padding: "10px 14px", background: S.accent2Light, borderRadius: 10 }}>{error}</p>}
          {info && <p style={{ color: S.accent, fontSize: "0.8rem", marginBottom: 12, padding: "10px 14px", background: S.accentLight, borderRadius: 10 }}>{info}</p>}
          <button type="submit" disabled={loading} style={{ width: "100%", marginTop: 8, fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "0.95rem", color: "#fff", background: loading ? "#999" : S.accent, border: "none", padding: "14px 28px", borderRadius: 100, cursor: loading ? "wait" : "pointer", boxShadow: "0 4px 16px rgba(45,106,79,0.25)" }}>
            {loading ? "Please wait..." : mode === "signup" ? "Create Account →" : mode === "forgot" ? "Send Reset Link →" : "Log In →"}
          </button>
        </form>

        {mode === "login" && (
          <p style={{ textAlign: "center", marginTop: 16, fontSize: "0.78rem" }}>
            <button onClick={() => { setMode("forgot"); setError(""); setInfo(""); }} style={{ background: "none", border: "none", color: S.accent, cursor: "pointer", fontWeight: 600, fontFamily: "'Inter', sans-serif" }}>Forgot password?</button>
          </p>
        )}
        {mode === "forgot" && (
          <p style={{ textAlign: "center", marginTop: 16, fontSize: "0.78rem" }}>
            <button onClick={() => { setMode("login"); setError(""); setInfo(""); }} style={{ background: "none", border: "none", color: S.accent, cursor: "pointer", fontWeight: 600, fontFamily: "'Inter', sans-serif" }}>← Back to log in</button>
          </p>
        )}
      </div>

      <p style={{ marginTop: 20, fontSize: "0.68rem", color: S.muted, textAlign: "center", maxWidth: 380, lineHeight: 1.6 }}>
        Educational. Yours. Not medical advice — always consult your physician.
      </p>
    </div>
  );
}

// ─── SIDEBAR NAV (responsive) ────────────────────────────────

export { AuthScreen };
